import {
    getCompanySourceById,
    HARDCODED_COMPANY_SOURCES,
    MM_FALLBACK_COMPANY_SOURCE_ID,
    resolveCompanySourceId,
    toApiCompanySourceId,
} from 'shared/companySource';
import {
    setDraftBooleanField,
    setDraftFloatField,
    setDraftIntegerField,
    setDraftStringField,
} from 'shared/utils/draftFieldParsers';

import {
    findWeekAndYearForScheduledDeliveryDate,
    formatPurchaseOrderDateForInput,
    formatPurchaseOrderDateTimeForInput,
} from './purchaseOrderDateUtils';
import { DELIVERY_TYPE_PICKUP, normalizeToUpperCase } from './purchaseOrderDisplayUtils';

const ALL_COMPANY_SOURCE_IDS = HARDCODED_COMPANY_SOURCES.map((source) => source.id);

/**
 * @param {Object|null|undefined} itemMetadata
 * @returns {number[]|null} null when companySources field is missing (unknown eligibility)
 */
function extractCompanySourceIdsFromItemMetadata(itemMetadata) {
    const raw = itemMetadata?.companySources;
    if (!Array.isArray(raw)) return null;
    return raw
        .map((entry) => Number(entry?.companySourceId ?? entry?.companySourceID))
        .filter((id) => Number.isFinite(id));
}

export const DEFAULT_DRAFT = {
    vendorId: null,
    deliveryType: '',
    isReceivedAtCrossDock: false,
    pickUpDate: '',
    originalRequestedDeliveryDate: '',
    scheduledDeliveryDate: '',
    scheduledTime: '00:00',
    shipperId: '',
    crossDockId: '',
    crossDockDeliveryDate: '',
    facilities: [],
    buyers: [],
    minTempReq: '',
    maxTempReq: '',
    estimatedFreightCost: '',
    specialInstructions: '',
    priority: false,
};

export const STORAGE_TEMP_ZONE_RANGES = {
    'Ambient (>40)': { min: 40, max: 60 },
    'Ambient (>55)': { min: 55, max: 60 },
    'Chilled (32-35)': { min: 32, max: 35 },
    'Frozen (0-31)': { min: 0, max: 31 },
    'Nightshade (50-55)': { min: 50, max: 55 },
    'Refrigerated (36-40)': { min: 36, max: 40 },
};

export function getTempRangeForStorageZone(storageTempZone) {
    if (!storageTempZone) return null;
    return STORAGE_TEMP_ZONE_RANGES[String(storageTempZone)] ?? null;
}

/**
 * Builds the editable primary draft shape for a purchase order.
 * This is intentionally "edit-only" normalization (input-friendly strings, ids, etc.).
 *
 * @param {Object|null|undefined} purchaseOrder
 * @returns {Object}
 */
export function buildPurchaseOrderPrimaryDraft(purchaseOrder) {
    return {
        deliveryType: normalizeToUpperCase(purchaseOrder?.deliveryType),
        pickupDate: formatPurchaseOrderDateForInput(purchaseOrder?.pickupDate),
        scheduledDeliveryDate: formatPurchaseOrderDateTimeForInput(purchaseOrder?.scheduledDeliveryDate),
        vendorId: purchaseOrder?.vendorID ?? purchaseOrder?.vendorId ?? null,
        shipperId: purchaseOrder?.shipperID ?? '',
        crossDockId: purchaseOrder?.crossDockID ?? '',
        crossDockDeliveryDate: formatPurchaseOrderDateForInput(purchaseOrder?.crossDockDeliveryDate),
        facilityId: purchaseOrder?.facilityID,
        buyers: Array.from(
            new Set(purchaseOrder?.buyers?.map((buyer) => buyer.authUserID).filter((id) => id != null) ?? []),
        ),
        minTempReq: purchaseOrder?.minTempReq ?? '',
        maxTempReq: purchaseOrder?.maxTempReq ?? '',
        specialInstructions: purchaseOrder?.specialInstructions ?? '',
        vendorPdfNote: purchaseOrder?.vendorPdfNote ?? '',
        estimatedFreightCost: purchaseOrder?.estimatedFreightCost ?? '',
        priority: purchaseOrder?.priority === true,
        reasonCode: purchaseOrder?.reasonCode ?? '',
    };
}

export function buildDuplicatePrimaryDraft(sourcePO) {
    if (!sourcePO) return { ...DEFAULT_DRAFT };

    return {
        vendorId: sourcePO?.vendorID ?? sourcePO?.vendorId ?? null,
        deliveryType: normalizeToUpperCase(sourcePO?.deliveryType) || '',
        crossDockId: sourcePO?.crossDockID ?? sourcePO?.crossDockId ?? '',
        isReceivedAtCrossDock: sourcePO?.isReceivedAtCrossDock === true,
        minTempReq: sourcePO?.minTempReq ?? '',
        maxTempReq: sourcePO?.maxTempReq ?? '',
        shipperId: sourcePO?.shipperID ?? sourcePO?.shipperId ?? '',
        buyers: Array.from(
            new Set(sourcePO?.buyers?.map((buyer) => buyer.authUserID ?? buyer.id).filter((id) => id != null) ?? []),
        ),
        priority: sourcePO?.priority === true,
        facilities: [],
        pickUpDate: '',
        originalRequestedDeliveryDate: '',
        scheduledDeliveryDate: '',
        scheduledTime: '00:00',
        crossDockDeliveryDate: '',
        specialInstructions: '',
        estimatedFreightCost: '',
    };
}

/**
 * Returns company source ids eligible for a procurement PO line item from item metadata.
 * No API call — reads companySources array on the item master row.
 *
 * @param {Object|null|undefined} itemMetadata - Item master row with companySources field
 * @returns {number[]}
 */
export function getEligibleCompanySourceIdsForPurchaseOrderItem(itemMetadata) {
    const extracted = extractCompanySourceIdsFromItemMetadata(itemMetadata);
    if (extracted === null) {
        return ALL_COMPANY_SOURCE_IDS;
    }
    if (extracted.length === 0) {
        return [];
    }
    const allowed = new Set(extracted);
    return ALL_COMPANY_SOURCE_IDS.filter((id) => allowed.has(id));
}

/**
 * Returns partner ids to pre-select when an item master is chosen in PO create.
 * - Misfits Market when it is among eligible partners
 * - Otherwise the sole partner when only one is eligible
 * - Otherwise none (multiple partners, none of which is MM)
 *
 * @param {Object|null|undefined} itemMetadata
 * @returns {number[]}
 */
export function getDefaultCompanySourceIdsForPurchaseOrderItem(itemMetadata) {
    if (!itemMetadata) return [];
    const eligible = getEligibleCompanySourceIdsForPurchaseOrderItem(itemMetadata);
    if (eligible.length === 0) return [];
    if (eligible.includes(MM_FALLBACK_COMPANY_SOURCE_ID)) return [MM_FALLBACK_COMPANY_SOURCE_ID];
    if (eligible.length === 1) return [eligible[0]];
    return [];
}

/**
 * True when item metadata explicitly lists companySources as an empty array (invalid eligibility).
 *
 * @param {Object|null|undefined} itemMetadata
 * @returns {boolean}
 */
export function hasInvalidEmptyCompanySourceEligibility(itemMetadata) {
    const extracted = extractCompanySourceIdsFromItemMetadata(itemMetadata);
    return extracted !== null && extracted.length === 0;
}

/**
 * Keeps only partner ids eligible for the item. When none remain, returns defaults for the item.
 *
 * @param {Array<number|string>} selectedCompanySourceIds
 * @param {Object|null|undefined} itemMetadata
 * @returns {number[]}
 */
export function reconcileSelectedCompanySourceIdsForPurchaseOrderItem(selectedCompanySourceIds, itemMetadata) {
    const eligible = getEligibleCompanySourceIdsForPurchaseOrderItem(itemMetadata);
    const eligibleSet = new Set(eligible);
    const reconciled = (Array.isArray(selectedCompanySourceIds) ? selectedCompanySourceIds : [])
        .map((id) => Number(id))
        .filter((id) => eligibleSet.has(id));
    if (reconciled.length > 0) return reconciled;
    return getDefaultCompanySourceIdsForPurchaseOrderItem(itemMetadata);
}

export function buildDuplicateItems(sourcePODetail) {
    const items = sourcePODetail?.items ?? sourcePODetail?.purchaseOrderItems ?? [];
    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item, index) => {
        const rawUnitPrice = item?.unitPrice ?? item?.casePrice;
        const numericUnitPrice =
            rawUnitPrice === null || rawUnitPrice === undefined || rawUnitPrice === '' ? null : Number(rawUnitPrice);
        const unitPrice = Number.isFinite(numericUnitPrice) ? numericUnitPrice.toFixed(2) : '';

        return {
            id: `dup-${Date.now()}-${index}`,
            sku: String(item?.sku ?? item?.itemMasterSku ?? item?.itemMasterID ?? ''),
            itemName: String(item?.itemName ?? item?.name ?? ''),
            expirationType: item?.expirationType ?? '',
            baseUnitsPerCase: item?.baseUnitsPerCase ?? '',
            expectedProductionDate: formatPurchaseOrderDateForInput(item?.expectedProductionDate) ?? '',
            expectedExpirationDate:
                formatPurchaseOrderDateForInput(item?.expectedExpirationDate ?? item?.expiresOn) ?? '',
            qty: item?.qty != null ? String(item.qty) : '',
            csPallet: item?.csPallet != null ? String(item.csPallet) : '',
            unitPrice,
            vendorSku: item?.vendorSku ?? '',
            year: item?.year != null ? String(item.year) : '',
            purchaseType: item?.purchaseType ?? '',
            week: '',
            suggestedRetailPrice: '',
            unpurchasedOutcome: '',
            sourcingReasons: [],
            countryOfOriginId: '',
            hasTempTale: item?.hasTempTale === true,
            companySourceId: resolveCompanySourceId(item) ?? MM_FALLBACK_COMPANY_SOURCE_ID,
        };
    });
}

/**
 * Builds a new PO line-item row from an item master match (create / edit add flows).
 *
 * @param {Object} params
 * @param {Object} params.match - Item master row
 * @param {number} [params.companySourceId]
 * @param {string} [params.vendorSku]
 * @param {string} [params.scheduledDeliveryDate]
 * @param {number} params.rowSequence - Monotonic id suffix for new row ids
 * @returns {Object}
 */
export function buildPurchaseOrderItemRowFromMaster({
    companySourceId = MM_FALLBACK_COMPANY_SOURCE_ID,
    match,
    rowSequence,
    scheduledDeliveryDate = '',
    vendorSku = '',
}) {
    const id = `new-${Date.now()}-${rowSequence}`;
    const weekYear = scheduledDeliveryDate ? findWeekAndYearForScheduledDeliveryDate(scheduledDeliveryDate) : null;

    return {
        id,
        sku: String(match.sku),
        externalSku: String(match.externalSku ?? ''),
        itemName: String(match.itemNameInternal ?? match.sku),
        expirationType: match.expirationType ?? '',
        baseUnitsPerCase: match.baseUnitsPerCase ?? '',
        expectedExpirationDate: '',
        expectedProductionDate: '',
        qty: '',
        vendorSku,
        csPallet: match.csPallet != null ? String(match.csPallet) : '',
        unitPrice: '',
        week: weekYear?.week ? String(weekYear.week) : '',
        year: weekYear?.year ? String(weekYear.year) : '',
        l1TaxonomyID: match.l1TaxonomyID,
        purchaseType: '',
        suggestedRetailPrice: '',
        unpurchasedOutcome: '',
        sourcingReasons: [],
        countryOfOriginId: '',
        hasTempTale: false,
        companySourceId: Number(companySourceId),
    };
}

/**
 * Determines if primary changes require a reason code.
 * Reason code is required when pickupDate, shipperId, deliveryType, or scheduledDeliveryDate change.
 *
 * @param {{initialPrimary: Object, currentPrimary: Object}} params
 * @returns {boolean}
 */
export function getPrimaryRequiresReasonCode({ currentPrimary, initialPrimary }) {
    const primaryDeliveryTypeChanged =
        normalizeToUpperCase(currentPrimary?.deliveryType) !== normalizeToUpperCase(initialPrimary?.deliveryType);
    const primaryPickupDateChanged =
        String(currentPrimary?.pickupDate ?? '') !== String(initialPrimary?.pickupDate ?? '');
    const primaryShipperChanged = String(currentPrimary?.shipperId ?? '') !== String(initialPrimary?.shipperId ?? '');
    const primaryScheduledDeliveryChanged =
        String(currentPrimary?.scheduledDeliveryDate ?? '') !== String(initialPrimary?.scheduledDeliveryDate ?? '');
    return (
        primaryDeliveryTypeChanged ||
        primaryPickupDateChanged ||
        primaryShipperChanged ||
        primaryScheduledDeliveryChanged
    );
}

/**
 * Builds the API payload for updating purchase order primary fields by diffing current vs initial.
 *
 * @param {{initialPrimary: Object, currentPrimary: Object}} params
 * @returns {Object} API payload (only changed fields)
 */
export function buildPurchaseOrderPrimaryUpdatePayload({ currentPrimary, initialPrimary }) {
    const payload = {};

    const fieldKeys = [
        'deliveryType',
        'pickupDate',
        'scheduledDeliveryDate',
        'vendorId',
        'shipperId',
        'crossDockId',
        'crossDockDeliveryDate',
        'facilityId',
        'minTempReq',
        'maxTempReq',
        'specialInstructions',
        'vendorPdfNote',
        'estimatedFreightCost',
    ];
    fieldKeys.forEach((key) => {
        if (
            currentPrimary?.[key] !== undefined &&
            String(currentPrimary?.[key] ?? '') !== String(initialPrimary?.[key] ?? '')
        ) {
            payload[key] = currentPrimary?.[key];
        }
    });

    if (
        Array.isArray(currentPrimary?.buyers) &&
        JSON.stringify(currentPrimary.buyers) !== JSON.stringify(initialPrimary?.buyers)
    ) {
        payload.buyers = currentPrimary.buyers;
    }

    if (currentPrimary?.priority !== initialPrimary?.priority) {
        payload.priority = currentPrimary?.priority;
    }

    const requiresReasonCode = getPrimaryRequiresReasonCode({ initialPrimary, currentPrimary });
    if (requiresReasonCode) {
        payload.reasonCode = currentPrimary?.reasonCode;
    }

    // Map to API field names
    const apiPayload = {};
    if (payload.deliveryType !== undefined) apiPayload.deliveryType = payload.deliveryType;
    if (payload.pickupDate !== undefined) apiPayload.pickupDate = payload.pickupDate;

    if (payload.scheduledDeliveryDate !== undefined) {
        const datetimeString = payload.scheduledDeliveryDate;
        if (datetimeString && String(datetimeString).includes('T')) {
            const [datePart, timePart] = String(datetimeString).split('T');
            apiPayload.scheduledDeliveryDate = datePart;
            apiPayload.deliverTime = timePart || '00:00';
        } else {
            apiPayload.scheduledDeliveryDate = datetimeString;
        }
    }

    if (payload.vendorId !== undefined) apiPayload.vendorID = payload.vendorId;
    if (payload.shipperId !== undefined)
        apiPayload.inboundShipperID = payload.shipperId === '' ? '' : payload.shipperId;
    if (payload.crossDockId !== undefined)
        apiPayload.crossDockID = payload.crossDockId === '' ? '' : payload.crossDockId;
    if (payload.crossDockDeliveryDate !== undefined) apiPayload.crossDockDeliveryDate = payload.crossDockDeliveryDate;
    if (payload.facilityId !== undefined) apiPayload.facilityID = payload.facilityId;
    if (payload.buyers !== undefined) apiPayload.buyers = payload.buyers;
    if (payload.minTempReq !== undefined) apiPayload.minTempReq = payload.minTempReq;
    if (payload.maxTempReq !== undefined) apiPayload.maxTempReq = payload.maxTempReq;
    if (payload.specialInstructions !== undefined) apiPayload.specialInstructions = payload.specialInstructions;
    if (payload.vendorPdfNote !== undefined) apiPayload.vendorPdfNote = payload.vendorPdfNote;
    if (payload.estimatedFreightCost !== undefined) apiPayload.estimatedFreightCost = payload.estimatedFreightCost;
    if (payload.priority !== undefined) apiPayload.priority = payload.priority;
    if (payload.reasonCode !== undefined) apiPayload.reasonCode = payload.reasonCode;

    return apiPayload;
}

/**
 * Normalizes and parses purchase order map item edits into the typed updates object.
 *
 * @param {Object} draft
 * @returns {Object}
 */
export function buildPurchaseOrderMapItemUpdates(draft) {
    const updates = {};

    setDraftStringField({ draft, updates, field: 'expectedExpirationDate' });
    setDraftStringField({ draft, updates, field: 'expectedProductionDate' });
    setDraftIntegerField({ draft, updates, field: 'qty' });
    setDraftStringField({ draft, updates, field: 'vendorSku' });
    setDraftIntegerField({ draft, updates, field: 'csPallet' });
    setDraftFloatField({ draft, updates, field: 'unitPrice' });
    setDraftIntegerField({ draft, updates, field: 'week' });
    setDraftIntegerField({ draft, updates, field: 'year' });
    setDraftStringField({ draft, updates, field: 'purchaseType' });
    setDraftFloatField({ draft, updates, field: 'suggestedRetailPrice' });
    setDraftStringField({ draft, updates, field: 'unpurchasedOutcome' });

    if (Object.prototype.hasOwnProperty.call(draft, 'sourcingReasons')) {
        const values = Array.isArray(draft.sourcingReasons) ? draft.sourcingReasons.filter(Boolean).map(String) : [];
        updates.sourcingReasons = values.slice().sort((a, b) => a.localeCompare(b));
    }

    setDraftIntegerField({ draft, updates, field: 'countryOfOriginId' });
    setDraftIntegerField({ draft, updates, field: 'companySourceId' });

    setDraftBooleanField({ draft, updates, field: 'hasTempTale' });

    return updates;
}

/**
 * Normalizes the purchase order map item id used across APIs and UI tables.
 *
 * @param {Object} item
 * @returns {string|null}
 */
export function getPoMapId(item) {
    const raw = item?.poMapID ?? item?.poMapId ?? item?.id ?? null;
    return raw != null ? String(raw) : null;
}

/**
 * Normalizes the row id used for item edit state and duplicate detection.
 *
 * @param {Object|null|undefined} row
 * @returns {string}
 */
export function getLineItemRowId(row) {
    return getPoMapId(row) ?? '';
}

/**
 * Computes a diff of item edits by comparing current UI draft values to the original item values.
 *
 * @param {{item: Object, uiDraft: Object}} params
 * @returns {Object} diff payload (only changed fields, normalized)
 */
export function buildPurchaseOrderMapItemDiff({ item, uiDraft }) {
    const updates = buildPurchaseOrderMapItemUpdates(uiDraft);

    const originalForCompare = buildPurchaseOrderMapItemUpdates({
        expectedExpirationDate: item?.expectedExpirationDate ?? item?.expiresOn ?? '',
        expectedProductionDate: item?.expectedProductionDate ?? '',
        qty: item?.qty ?? '',
        vendorSku: item?.vendorSku ?? '',
        csPallet: item?.csPallet ?? '',
        unitPrice: item?.unitPrice ?? item?.casePrice ?? '',
        week: item?.week ?? item?.shipWeek ?? '',
        year: item?.year ?? item?.shipYear ?? '',
        purchaseType: item?.purchaseType ?? '',
        suggestedRetailPrice: item?.suggestedRetailPrice ?? item?.msrpPrice ?? '',
        unpurchasedOutcome: item?.unpurchasedOutcome ?? item?.whereWouldItGo ?? '',
        sourcingReasons: Array.isArray(item?.sourcingReasons) ? item.sourcingReasons : [],
        countryOfOriginId: item?.countryOfOriginId ?? item?.countryOfOriginID ?? '',
        companySourceId: resolveCompanySourceId(item) ?? MM_FALLBACK_COMPANY_SOURCE_ID,
        hasTempTale: item?.hasTempTale === true,
    });

    const diff = {};
    Object.entries(updates).forEach(([key, value]) => {
        if (key === 'sourcingReasons') {
            const a = Array.isArray(value) ? value : [];
            const b = Array.isArray(originalForCompare.sourcingReasons) ? originalForCompare.sourcingReasons : [];
            if (JSON.stringify(a) !== JSON.stringify(b)) diff[key] = a;
            return;
        }
        if (key === 'hasTempTale') {
            const next = value === true;
            const prev = originalForCompare.hasTempTale === true;
            if (next !== prev) diff[key] = next;
            return;
        }
        if (String(value ?? '') !== String(originalForCompare[key] ?? '')) {
            diff[key] = value;
        }
    });

    return diff;
}

export function getItemEditInputValue(itemEditStateRef, poMapId, field) {
    const entry = itemEditStateRef?.current?.inputs?.[poMapId]?.[field];
    if (entry === undefined) return undefined;
    if (entry && typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'value')) {
        return entry.value;
    }
    return entry;
}

export function getItemEditSelectValue(itemEditStateRef, poMapId, field) {
    return itemEditStateRef?.current?.selects?.[poMapId]?.[field];
}

export function getItemEditMultiValue(itemEditStateRef, poMapId, field) {
    return itemEditStateRef?.current?.multi?.[poMapId]?.[field];
}

/**
 * Builds the API payload row for adding a new item to an existing purchase order in edit mode.
 *
 * @param {{item: Object, itemEditStateRef: Object, reasonCode: string}} params
 * @returns {Object}
 */
export function buildPurchaseOrderAddItemPayloadRow({ item, itemEditStateRef, reasonCode }) {
    const rowId = String(item?.id ?? '');
    const selectCompanySourceId = getItemEditSelectValue(itemEditStateRef, rowId, 'companySourceId');
    const mergedForCompanySource = {
        ...item,
        ...(selectCompanySourceId !== undefined ? { companySourceId: selectCompanySourceId } : {}),
    };

    const hasTempTaleValue = getItemEditInputValue(itemEditStateRef, rowId, 'hasTempTale');
    const hasTempTale =
        hasTempTaleValue === undefined
            ? item?.hasTempTale === true
            : hasTempTaleValue === true || hasTempTaleValue === 'true';

    return {
        sku: item.sku,
        qty: getItemEditInputValue(itemEditStateRef, rowId, 'qty'),
        unitPrice: getItemEditInputValue(itemEditStateRef, rowId, 'unitPrice'),
        vendorSku: getItemEditInputValue(itemEditStateRef, rowId, 'vendorSku'),
        csPallet: getItemEditInputValue(itemEditStateRef, rowId, 'csPallet'),
        week: getItemEditInputValue(itemEditStateRef, rowId, 'week'),
        year: getItemEditInputValue(itemEditStateRef, rowId, 'year'),
        suggestedRetailPrice: getItemEditInputValue(itemEditStateRef, rowId, 'suggestedRetailPrice'),
        expectedExpirationDate: getItemEditInputValue(itemEditStateRef, rowId, 'expectedExpirationDate'),
        expectedProductionDate: getItemEditInputValue(itemEditStateRef, rowId, 'expectedProductionDate'),
        purchaseType: getItemEditSelectValue(itemEditStateRef, rowId, 'purchaseType'),
        unpurchasedOutcome: getItemEditSelectValue(itemEditStateRef, rowId, 'unpurchasedOutcome'),
        countryOfOriginId: getItemEditSelectValue(itemEditStateRef, rowId, 'countryOfOriginId'),
        sourcingReasons: getItemEditMultiValue(itemEditStateRef, rowId, 'sourcingReasons') ?? [],
        hasTempTale,
        companySourceId: toApiCompanySourceId(mergedForCompanySource) ?? MM_FALLBACK_COMPANY_SOURCE_ID,
        reasonCode,
    };
}

// -----------------------------
// Create + Edit shared helpers
// -----------------------------

export const toIntOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : null;
};

export const toFloatOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const normalizeItemBoolean = (value) => {
    if (value === true || value === 1 || value === '1') return true;
    const s = String(value ?? '')
        .trim()
        .toLowerCase();
    if (s === 'true' || s === 'yes' || s === 'y') return true;
    return false;
};

export const DUPLICATE_LINE_ITEM_MESSAGE =
    'Each combination of item master SKU, case price, and company source can only appear once on a purchase order. Expiration or production date also applies when the item type requires it. Remove the duplicate line or change the distinguishing fields on one of the lines.';

const DUPLICATE_LINE_ITEM_SUMMARY_MESSAGES = new Set([DUPLICATE_LINE_ITEM_MESSAGE]);

/**
 * @param {string} message
 * @returns {boolean}
 */
export function isDuplicateLineItemSummaryMessage(message) {
    return DUPLICATE_LINE_ITEM_SUMMARY_MESSAGES.has(message);
}

/**
 * @param {{ messages?: string[] }} itemError
 * @returns {boolean}
 */
export function isDuplicateLineItemRowError(itemError) {
    return (Array.isArray(itemError?.messages) ? itemError.messages : []).some((message) =>
        String(message).includes('appears on more than one line'),
    );
}

/**
 * @param {Array<{ id: string|number, sku?: string, messages: string[] }>} itemErrors
 * @returns {Array<{ id: string|number, sku?: string, messages: string[] }>}
 */
export function filterDuplicateLineItemRowErrors(itemErrors) {
    return (Array.isArray(itemErrors) ? itemErrors : []).filter(isDuplicateLineItemRowError);
}

/**
 * Builds item error alert rows without duplicating live duplicate feedback and save validation.
 * Before save: live addItemError summaries plus duplicate row detail only.
 * After save attempt: validation-owned itemSummaryErrors and itemErrors only.
 *
 * @param {Object} params
 * @param {boolean} [params.showValidation]
 * @param {string} [params.addItemError]
 * @param {string[]} [params.itemSummaryErrors]
 * @param {Array<{ id: string|number, sku?: string, messages: string[] }>} [params.itemErrors]
 * @returns {Array<{ key: string, message: string }>}
 */
export function buildDisplayedItemErrorMessages({
    addItemError = '',
    itemErrors = [],
    itemSummaryErrors = [],
    showValidation = false,
}) {
    const showErrors = showValidation === true;

    if (showErrors) {
        return [
            ...itemSummaryErrors.map((message) => ({ key: `summary-${message}`, message })),
            ...itemErrors.flatMap((error) =>
                error.messages.map((message) => ({
                    key: `item-${error.id}-${message}`,
                    message,
                })),
            ),
        ];
    }

    if (!addItemError) return [];

    const messages = [{ key: `add-item-${addItemError}`, message: addItemError }];
    filterDuplicateLineItemRowErrors(itemErrors).forEach((error) => {
        error.messages.forEach((message) => {
            messages.push({ key: `item-${error.id}-${message}`, message });
        });
    });
    return messages;
}

/**
 * @param {string} expirationType
 * @returns {boolean}
 */
export function isLineItemExpirationDateEditable(expirationType) {
    return String(expirationType ?? '') === 'EXPIRATION_DATE';
}

/**
 * @param {string} expirationType
 * @returns {boolean}
 */
export function isLineItemProductionDateEditable(expirationType) {
    return String(expirationType ?? '') === 'PRODUCTION_DATE';
}

/**
 * Resolves expiration type for duplicate detection from row data or item master lookup.
 *
 * @param {Object|null|undefined} row
 * @param {Map<string, Object>|null|undefined} [itemMastersBySku]
 * @returns {string}
 */
export function resolveLineItemExpirationType(row, itemMastersBySku) {
    const fromRow = row?.expirationType;
    if (fromRow !== null && fromRow !== undefined && String(fromRow) !== '') {
        return String(fromRow);
    }

    const sku = row?.sku ?? row?.itemMasterSku ?? row?.itemMasterID;
    if (sku != null && itemMastersBySku instanceof Map) {
        const master = itemMastersBySku.get(String(sku));
        if (master?.expirationType != null && String(master.expirationType) !== '') {
            return String(master.expirationType);
        }
    }

    return '';
}

/**
 * @param {Object|null|undefined} row
 * @param {Object|null|undefined} itemEditStateRef
 * @returns {number|null}
 */
function getEffectiveLineItemUnitPrice(row, itemEditStateRef) {
    const rowId = getLineItemRowId(row);
    const unitPriceInput = getItemEditInputValue(itemEditStateRef, rowId, 'unitPrice');
    const rawPrice = unitPriceInput !== undefined ? unitPriceInput : (row?.unitPrice ?? row?.casePrice);
    return toFloatOrNull(rawPrice);
}

/**
 * @param {Object|null|undefined} row
 * @param {Object|null|undefined} itemEditStateRef
 * @param {string} field
 * @param {string} rowFallback
 * @returns {string|null}
 */
function getEffectiveLineItemNormalizedDate(row, itemEditStateRef, field, rowFallback) {
    const rowId = getLineItemRowId(row);
    const dateInput = getItemEditInputValue(itemEditStateRef, rowId, field);
    const rawDate = dateInput !== undefined ? dateInput : rowFallback;
    const normalized = formatPurchaseOrderDateForInput(rawDate);
    return normalized && normalized !== '' ? normalized : null;
}

/**
 * @param {string[]} itemSummaryErrors
 * @returns {string}
 */
export function formatDuplicateLineItemLiveSummary(itemSummaryErrors) {
    return (Array.isArray(itemSummaryErrors) ? itemSummaryErrors : []).join(' ');
}

/**
 * Resolves the effective partner id for a line item including inline edit state.
 *
 * @param {Object|null|undefined} row
 * @param {Object|null|undefined} itemEditStateRef
 * @returns {number}
 */
function resolveLineItemCompanySourceId(row, itemEditStateRef) {
    const rowId = getLineItemRowId(row);
    const selectValue = getItemEditSelectValue(itemEditStateRef, rowId, 'companySourceId');
    const merged = {
        ...row,
        ...(selectValue !== undefined ? { companySourceId: selectValue } : {}),
    };
    return toApiCompanySourceId(merged) ?? MM_FALLBACK_COMPANY_SOURCE_ID;
}

/**
 * Builds a stable uniqueness key for PO line-item duplicate detection.
 * Returns null when case price is unset (row excluded from duplicate detection).
 *
 * @param {Object|null|undefined} row
 * @param {{ itemEditStateRef?: Object, itemMastersBySku?: Map<string, Object> }} [options]
 * @returns {string|null}
 */
export function buildLineItemUniquenessKey(row, { itemEditStateRef, itemMastersBySku } = {}) {
    const sku = String(row?.sku ?? '');
    const unitPrice = getEffectiveLineItemUnitPrice(row, itemEditStateRef);
    if (unitPrice === null) return null;

    const expirationType = resolveLineItemExpirationType(row, itemMastersBySku);
    let dateKey = 'null';
    if (expirationType === 'EXPIRATION_DATE') {
        dateKey =
            getEffectiveLineItemNormalizedDate(
                row,
                itemEditStateRef,
                'expectedExpirationDate',
                row?.expectedExpirationDate ?? row?.expiresOn ?? '',
            ) ?? 'null';
    } else if (expirationType === 'PRODUCTION_DATE') {
        dateKey =
            getEffectiveLineItemNormalizedDate(
                row,
                itemEditStateRef,
                'expectedProductionDate',
                row?.expectedProductionDate ?? '',
            ) ?? 'null';
    }

    const companySourceId = resolveLineItemCompanySourceId(row, itemEditStateRef);
    return `${sku}::${unitPrice}::${dateKey}::${companySourceId}`;
}

/**
 * @param {number} companySourceId
 * @returns {string}
 */
function formatCompanySourceDisplayName(companySourceId) {
    return getCompanySourceById(companySourceId)?.displayName ?? `Company Source ${companySourceId}`;
}

/**
 * @param {Object} row
 * @param {Object|null|undefined} itemEditStateRef
 * @param {Map<string, Object>|null|undefined} [itemMastersBySku]
 * @returns {string}
 */
function formatLineItemDateLabelForDuplicateMessage(row, itemEditStateRef, itemMastersBySku) {
    const expirationType = resolveLineItemExpirationType(row, itemMastersBySku);

    if (expirationType === 'EXPIRATION_DATE') {
        const normalized = getEffectiveLineItemNormalizedDate(
            row,
            itemEditStateRef,
            'expectedExpirationDate',
            row?.expectedExpirationDate ?? row?.expiresOn ?? '',
        );
        return normalized ? `expiration date ${normalized}` : 'expiration date not set';
    }
    if (expirationType === 'PRODUCTION_DATE') {
        const normalized = getEffectiveLineItemNormalizedDate(
            row,
            itemEditStateRef,
            'expectedProductionDate',
            row?.expectedProductionDate ?? '',
        );
        return normalized ? `production date ${normalized}` : 'production date not set';
    }
    return '';
}

/**
 * @param {Object} row
 * @param {Object|null|undefined} itemEditStateRef
 * @param {Map<string, Object>|null|undefined} [itemMastersBySku]
 * @returns {string}
 */
function buildDuplicateLineItemDetailMessage(row, itemEditStateRef, itemMastersBySku) {
    const expirationType = resolveLineItemExpirationType(row, itemMastersBySku);
    const sku = String(row?.sku ?? '');
    const companySourceId = resolveLineItemCompanySourceId(row, itemEditStateRef);
    const companySourceName = formatCompanySourceDisplayName(companySourceId);
    const unitPrice = getEffectiveLineItemUnitPrice(row, itemEditStateRef);
    const base = `SKU ${sku || '(unknown)'} with ${companySourceName} and case price ${unitPrice}`;

    if (expirationType === 'EXPIRATION_DATE') {
        const dateLabel = formatLineItemDateLabelForDuplicateMessage(row, itemEditStateRef, itemMastersBySku);
        return `${base} and ${dateLabel} appears on more than one line. Change case price, expiration date, or company source, or remove one of the lines.`;
    }
    if (expirationType === 'PRODUCTION_DATE') {
        const dateLabel = formatLineItemDateLabelForDuplicateMessage(row, itemEditStateRef, itemMastersBySku);
        return `${base} and ${dateLabel} appears on more than one line. Change case price, production date, or company source, or remove one of the lines.`;
    }
    if (expirationType === 'DAYS_AFTER_RECEIPT') {
        return `${base} appears on more than one line. This item uses days-after-receipt expiration, so dates are calculated at receipt and cannot be used to distinguish lines. Change case price or company source, or remove one of the lines.`;
    }
    if (expirationType === 'NO_EXPIRATION_DATE') {
        return `${base} appears on more than one line. This item has no expiration date, so dates cannot be used to distinguish lines. Change case price or company source, or remove one of the lines.`;
    }
    return `${base} appears on more than one line. Change case price or company source, or remove one of the lines.`;
}

/**
 * @param {Array<{ id: string|number, sku?: string, messages: string[] }>} existing
 * @param {Array<{ id: string|number, sku?: string, messages: string[] }>} additional
 * @returns {Array<{ id: string|number, sku?: string, messages: string[] }>}
 */
function mergeItemErrors(existing, additional) {
    const byId = new Map(
        (Array.isArray(existing) ? existing : []).map((error) => [
            String(error.id),
            { ...error, messages: [...error.messages] },
        ]),
    );

    (Array.isArray(additional) ? additional : []).forEach((error) => {
        const id = String(error.id);
        if (byId.has(id)) {
            byId.get(id).messages.push(...error.messages);
        } else {
            byId.set(id, { ...error, messages: [...error.messages] });
        }
    });

    return Array.from(byId.values());
}

/**
 * Validates that active rows do not contain duplicate line-item combinations.
 *
 * @param {Object} params
 * @param {Map<string, Object>|null|undefined} [params.itemMastersBySku]
 * @returns {{ itemErrors: Array<{ id: string, sku: string, messages: string[] }>, itemSummaryErrors: string[] }}
 */
export function validateDuplicateLineItems({ itemEditStateRef, itemMastersBySku, removedItemIds, rows }) {
    const removed = removedItemIds instanceof Set ? removedItemIds : new Set();
    const activeRows = (Array.isArray(rows) ? rows : []).filter((row) => {
        const rowId = getLineItemRowId(row);
        return rowId && !removed.has(rowId);
    });

    const keyToRows = new Map();
    activeRows.forEach((row) => {
        const key = buildLineItemUniquenessKey(row, { itemEditStateRef, itemMastersBySku });
        if (key === null) return;
        if (!keyToRows.has(key)) keyToRows.set(key, []);
        keyToRows.get(key).push(row);
    });

    const duplicateGroups = [...keyToRows.values()].filter((group) => group.length > 1);
    if (duplicateGroups.length === 0) {
        return { itemErrors: [], itemSummaryErrors: [] };
    }

    const itemSummaryErrors = [DUPLICATE_LINE_ITEM_MESSAGE];
    const itemErrors = [];

    duplicateGroups.forEach((group) => {
        const sample = group[0];
        const sku = String(sample?.sku ?? '');
        const detailMessage = buildDuplicateLineItemDetailMessage(sample, itemEditStateRef, itemMastersBySku);

        group.forEach((row) => {
            const duplicateRowId = getLineItemRowId(row);
            itemErrors.push({
                id: duplicateRowId,
                sku: sku ? String(sku) : '',
                messages: [detailMessage],
            });
        });
    });

    return { itemErrors, itemSummaryErrors };
}

export const normalizeItemsForSubmit = ({ itemEditStateRef, items, removedItemIds }) => {
    const removed = removedItemIds instanceof Set ? removedItemIds : new Set();
    const inputsById = itemEditStateRef?.current?.inputs ?? {};
    const selectsById = itemEditStateRef?.current?.selects ?? {};
    const multiById = itemEditStateRef?.current?.multi ?? {};

    return (Array.isArray(items) ? items : [])
        .filter((row) => {
            const rowId = getPoMapId(row);
            return rowId && !removed.has(rowId);
        })
        .map((row) => {
            const rowId = getPoMapId(row);
            const inputs = inputsById[rowId] ?? {};
            const selects = selectsById[rowId] ?? {};
            const multi = multiById[rowId] ?? {};

            const merged = {
                ...row,
                ...inputs,
                ...selects,
                ...multi,
            };

            return {
                sku: toIntOrNull(merged.sku) ?? merged.sku,
                itemName: merged.itemName,
                expirationType: merged.expirationType ?? '',
                qty: toIntOrNull(merged.qty),
                vendorSku: merged.vendorSku ?? '',
                csPallet: toIntOrNull(merged.csPallet),
                unitPrice: toFloatOrNull(merged.unitPrice),
                expectedExpirationDate: merged.expectedExpirationDate ?? merged.expiresOn ?? '',
                expectedProductionDate: merged.expectedProductionDate ?? '',
                week: toIntOrNull(merged.week),
                year: toIntOrNull(merged.year),
                purchaseType: merged.purchaseType ?? '',
                suggestedRetailPrice: toFloatOrNull(merged.suggestedRetailPrice),
                unpurchasedOutcome: merged.unpurchasedOutcome ?? '',
                sourcingReasons: Array.isArray(merged.sourcingReasons) ? merged.sourcingReasons : [],
                countryOfOriginId: toIntOrNull(merged.countryOfOriginId),
                hasTempTale: normalizeItemBoolean(merged.hasTempTale),
                companySourceId: toApiCompanySourceId(merged) ?? MM_FALLBACK_COMPANY_SOURCE_ID,
            };
        });
};

export const validatePurchaseOrderCreate = ({ draft, itemEditStateRef, itemMastersBySku, items, removedItemIds }) => {
    const primaryErrors = [];
    const primaryFieldErrors = {};
    const itemErrors = [];
    const itemSummaryErrors = [];

    const submitItems = normalizeItemsForSubmit({ itemEditStateRef, items, removedItemIds });
    const deliveryDate = draft?.scheduledDeliveryDate ? new Date(`${draft.scheduledDeliveryDate}T00:00:00`) : null;

    if (!submitItems.length) {
        itemSummaryErrors.push('Add at least 1 item to proceed');
    }

    if (!draft?.vendorId) {
        primaryFieldErrors.vendorId = 'Vendor is required';
        primaryErrors.push(primaryFieldErrors.vendorId);
    }
    if (!draft?.deliveryType) {
        primaryFieldErrors.deliveryType = 'Delivery Type is required';
        primaryErrors.push(primaryFieldErrors.deliveryType);
    }
    if (draft?.deliveryType === DELIVERY_TYPE_PICKUP && !draft?.pickUpDate) {
        primaryFieldErrors.pickUpDate = 'Pick Up Date is required when Delivery Type is PICK UP';
        primaryErrors.push(primaryFieldErrors.pickUpDate);
    }
    if (draft?.deliveryType === DELIVERY_TYPE_PICKUP && !draft?.shipperId) {
        primaryFieldErrors.shipperId = 'Shipper is required when Delivery Type is PICK UP';
        primaryErrors.push(primaryFieldErrors.shipperId);
    }
    if (!draft?.originalRequestedDeliveryDate) {
        primaryFieldErrors.originalRequestedDeliveryDate = 'Original Requested Delivery Date is required';
        primaryErrors.push(primaryFieldErrors.originalRequestedDeliveryDate);
    }
    if (!draft?.scheduledDeliveryDate) {
        primaryFieldErrors.scheduledDeliveryDate = 'Scheduled Delivery Date is required';
        primaryErrors.push(primaryFieldErrors.scheduledDeliveryDate);
    }
    if (!draft?.scheduledTime) {
        primaryFieldErrors.scheduledTime = 'Scheduled Delivery Time is required';
        primaryErrors.push(primaryFieldErrors.scheduledTime);
    }

    if (deliveryDate) {
        const now = new Date();
        const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
        if (deliveryDate > oneYearFromNow) {
            primaryFieldErrors.scheduledDeliveryDate =
                'Scheduled Delivery Date cannot be more than 1 year in the future';
            primaryErrors.push(primaryFieldErrors.scheduledDeliveryDate);
        }
    }
    if (!Array.isArray(draft?.facilities) || draft.facilities.length === 0) {
        primaryFieldErrors.facilities = 'Facility is required';
        primaryErrors.push(primaryFieldErrors.facilities);
    }
    if (!Array.isArray(draft?.buyers) || draft.buyers.length === 0) {
        primaryFieldErrors.buyers = 'Buyer(s) are required';
        primaryErrors.push(primaryFieldErrors.buyers);
    }
    if (draft?.crossDockId && !draft?.crossDockDeliveryDate) {
        primaryFieldErrors.crossDockDeliveryDate = 'Cross Dock Delivery Date is required when Cross Dock is selected';
        primaryErrors.push(primaryFieldErrors.crossDockDeliveryDate);
    }
    if (draft?.crossDockDeliveryDate && !draft?.crossDockId) {
        primaryFieldErrors.crossDockId = 'Cross Dock is required when Cross Dock Delivery Date is provided';
        primaryErrors.push(primaryFieldErrors.crossDockId);
    }
    if (draft?.isReceivedAtCrossDock === true && !draft?.crossDockId) {
        primaryFieldErrors.crossDockId = 'Cross Dock is required when Received at Cross Dock is selected';
        primaryErrors.push(primaryFieldErrors.crossDockId);
    }
    if (
        draft?.minTempReq !== '' &&
        draft?.minTempReq != null &&
        draft?.maxTempReq !== '' &&
        draft?.maxTempReq != null
    ) {
        const minTemp = Number(draft.minTempReq);
        const maxTemp = Number(draft.maxTempReq);
        if (Number.isFinite(minTemp) && Number.isFinite(maxTemp) && minTemp > maxTemp) {
            primaryFieldErrors.minTempReq = 'Minimum temperature cannot be greater than maximum temperature';
            primaryErrors.push(primaryFieldErrors.minTempReq);
        }
    }

    const activeItems = Array.isArray(items)
        ? items.filter((row) => {
              const rowId = getPoMapId(row);
              return rowId && !removedItemIds?.has?.(rowId);
          })
        : [];
    submitItems.forEach((item, index) => {
        const sourceItem = activeItems[index];
        const displaySku = item?.sku ?? sourceItem?.sku ?? sourceItem?.itemMasterSku ?? '';
        const displayLabel = displaySku ? `SKU ${displaySku}` : `Row ${index + 1}`;
        const itemMessages = [];

        if (!item?.sku) {
            itemMessages.push(`Item Master SKU is required for ${displayLabel}`);
        }
        if (!item?.qty) {
            itemMessages.push(`Please fill in item quantity for ${displayLabel}`);
        }
        if (!item?.week) {
            itemMessages.push(`Please fill in item week number for ${displayLabel}`);
        }
        if (!item?.year) {
            itemMessages.push(`Please fill in item year for ${displayLabel}`);
        }
        if (!item?.purchaseType) {
            itemMessages.push(`Please select purchase type for ${displayLabel}`);
        }
        const isOpportunistic = String(item?.purchaseType ?? '') === 'OPPORTUNISTIC';
        const expirationType = String(item?.expirationType ?? '');
        const requiresExpirationDate = expirationType === 'EXPIRATION_DATE' && isOpportunistic;
        const requiresProductionDate = expirationType === 'PRODUCTION_DATE' && isOpportunistic;

        if (requiresExpirationDate && !item?.expectedExpirationDate) {
            itemMessages.push(`Please fill in expected expiration date for ${displayLabel}`);
        }

        if (requiresProductionDate && !item?.expectedProductionDate) {
            itemMessages.push(`Please fill in expected production date for ${displayLabel}`);
        }
        if (item?.csPallet !== 0 && !item?.csPallet) {
            itemMessages.push(`Please fill in cases per pallet for ${displayLabel}`);
        }
        if (item?.unitPrice !== 0 && !item?.unitPrice) {
            itemMessages.push(`Please fill in case price for ${displayLabel}`);
        }

        if (itemMessages.length > 0) {
            itemErrors.push({
                id: sourceItem?.id ?? item?.id ?? `row-${index + 1}`,
                sku: displaySku ? String(displaySku) : '',
                messages: itemMessages,
            });
        }
    });

    const duplicateValidation = validateDuplicateLineItems({
        rows: items,
        itemEditStateRef,
        itemMastersBySku,
        removedItemIds,
    });
    duplicateValidation.itemSummaryErrors.forEach((message) => {
        if (!itemSummaryErrors.includes(message)) {
            itemSummaryErrors.push(message);
        }
    });
    const mergedItemErrors = mergeItemErrors(itemErrors, duplicateValidation.itemErrors);

    const summaryErrors = [
        ...primaryErrors,
        ...itemSummaryErrors,
        ...mergedItemErrors.flatMap((error) => error.messages),
    ];
    const isValid = summaryErrors.length === 0;

    return {
        isValid,
        primaryErrors,
        primaryFieldErrors,
        itemErrors: mergedItemErrors,
        itemSummaryErrors,
        summaryErrors,
        submitItems,
    };
};

const deriveEditPrimaryState = ({ primaryDraft, primaryPurchaseOrder, purchaseOrderDetail }) => {
    const initialPrimary =
        buildPurchaseOrderPrimaryDraft({
            ...(primaryPurchaseOrder ?? {}),
            vendorPdfNote: purchaseOrderDetail?.vendorPdfNote ?? primaryPurchaseOrder?.vendorPdfNote ?? '',
        }) ?? {};
    const currentPrimary = primaryDraft ?? initialPrimary;
    const primaryRequiresReasonCode = getPrimaryRequiresReasonCode({ initialPrimary, currentPrimary });
    const scheduledDeliveryRaw = String(currentPrimary?.scheduledDeliveryDate ?? '');
    const deliveryDate = scheduledDeliveryRaw
        ? new Date(scheduledDeliveryRaw.includes('T') ? scheduledDeliveryRaw : `${scheduledDeliveryRaw}T00:00:00`)
        : null;

    return {
        currentPrimary,
        deliveryDate,
        primaryRequiresReasonCode,
        scheduledDeliveryRaw,
    };
};

const validateEditPrimaryFields = ({
    currentPrimary,
    deliveryDate,
    primaryRequiresReasonCode,
    scheduledDeliveryRaw,
}) => {
    const primaryErrors = [];
    const primaryFieldErrors = {};
    const reasonCodeErrors = {};

    if (primaryRequiresReasonCode && !currentPrimary?.reasonCode) {
        reasonCodeErrors.primary =
            'Reason code is required when changing pickup date, scheduled delivery, shipper, or delivery type';
        primaryErrors.push(reasonCodeErrors.primary);
        primaryFieldErrors.reasonCode = reasonCodeErrors.primary;
    }
    if (currentPrimary?.deliveryType === DELIVERY_TYPE_PICKUP && !currentPrimary?.pickupDate) {
        primaryFieldErrors.pickupDate = 'Pick Up Date is required when Delivery Type is PICK UP';
        primaryErrors.push(primaryFieldErrors.pickupDate);
    }
    if (currentPrimary?.deliveryType === DELIVERY_TYPE_PICKUP && !currentPrimary?.shipperId) {
        primaryFieldErrors.shipperId = 'Shipper is required when Delivery Type is PICK UP';
        primaryErrors.push(primaryFieldErrors.shipperId);
    }
    if (!Array.isArray(currentPrimary?.buyers) || currentPrimary.buyers.length === 0) {
        primaryFieldErrors.buyers = 'Buyer(s) are required';
        primaryErrors.push(primaryFieldErrors.buyers);
    }
    if (currentPrimary?.crossDockId && !currentPrimary?.crossDockDeliveryDate) {
        primaryFieldErrors.crossDockDeliveryDate = 'Cross Dock Delivery Date is required when Cross Dock is selected';
        primaryErrors.push(primaryFieldErrors.crossDockDeliveryDate);
    }
    if (currentPrimary?.crossDockDeliveryDate && !currentPrimary?.crossDockId) {
        primaryFieldErrors.crossDockId = 'Cross Dock is required when Cross Dock Delivery Date is provided';
        primaryErrors.push(primaryFieldErrors.crossDockId);
    }
    if (scheduledDeliveryRaw && deliveryDate && Number.isNaN(deliveryDate.getTime())) {
        primaryFieldErrors.scheduledDeliveryDate = 'Scheduled Delivery Date must be valid';
        primaryErrors.push(primaryFieldErrors.scheduledDeliveryDate);
    }
    if (deliveryDate) {
        const now = new Date();
        const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
        if (deliveryDate > oneYearFromNow) {
            primaryFieldErrors.scheduledDeliveryDate =
                'Scheduled Delivery Date cannot be more than 1 year in the future';
            primaryErrors.push(primaryFieldErrors.scheduledDeliveryDate);
        }
    }
    if (
        currentPrimary?.minTempReq !== '' &&
        currentPrimary?.minTempReq != null &&
        currentPrimary?.maxTempReq !== '' &&
        currentPrimary?.maxTempReq != null
    ) {
        const minTemp = Number(currentPrimary.minTempReq);
        const maxTemp = Number(currentPrimary.maxTempReq);
        if (Number.isFinite(minTemp) && Number.isFinite(maxTemp) && minTemp > maxTemp) {
            primaryFieldErrors.minTempReq = 'Minimum temperature cannot be greater than maximum temperature';
            primaryErrors.push(primaryFieldErrors.minTempReq);
        }
    }

    return {
        primaryErrors,
        primaryFieldErrors,
        reasonCodeErrors,
    };
};

const buildEditItemChangeSet = ({ addedItems, itemEditStateRef, purchaseOrderDetail, removedItemIds }) => {
    const addedItemIds = new Set((addedItems ?? []).map((item) => String(item?.id)));
    const newItemsToAdd = (addedItems ?? []).filter((item) => !removedItemIds?.has?.(String(item?.id)));
    const hasAdditions = newItemsToAdd.length > 0;

    const removalIds = Array.from(removedItemIds ?? []).filter((id) => !addedItemIds.has(String(id)));
    const hasRemovals = removalIds.length > 0;

    const items = purchaseOrderDetail?.items ?? purchaseOrderDetail?.purchaseOrderItems ?? [];
    const activeItemCount =
        (Array.isArray(items) ? items : [])
            .map((item) => getPoMapId(item))
            .filter((id) => id && !removedItemIds?.has?.(id)).length +
        (Array.isArray(addedItems) ? addedItems : []).filter((item) => !removedItemIds?.has?.(String(item?.id))).length;

    const itemUpdateEntries = (Array.isArray(items) ? items : [])
        .map((item) => {
            const poMapId = getPoMapId(item);
            if (!poMapId) return null;
            if (removedItemIds?.has?.(poMapId)) return null;

            const draft = {};

            const itemEditInputFields = [
                'expectedExpirationDate',
                'expectedProductionDate',
                'qty',
                'vendorSku',
                'csPallet',
                'unitPrice',
                'week',
                'year',
                'suggestedRetailPrice',
                'hasTempTale',
            ];

            const itemEditSelectFields = ['purchaseType', 'unpurchasedOutcome', 'countryOfOriginId', 'companySourceId'];

            itemEditInputFields.forEach((field) => {
                const value = getItemEditInputValue(itemEditStateRef, poMapId, field);
                if (value !== undefined) {
                    draft[field] = value;
                }
            });

            itemEditSelectFields.forEach((field) => {
                const value = getItemEditSelectValue(itemEditStateRef, poMapId, field);
                if (value !== undefined) {
                    draft[field] = value;
                }
            });

            const sourcing = getItemEditMultiValue(itemEditStateRef, poMapId, 'sourcingReasons');
            if (sourcing !== undefined) {
                draft.sourcingReasons = sourcing;
            }

            const diff = buildPurchaseOrderMapItemDiff({ item, uiDraft: draft });
            if (Object.keys(diff).length === 0) return null;
            return [poMapId, diff];
        })
        .filter(Boolean);

    const hasQtyUpdates = itemUpdateEntries.some(([, updates]) => Object.prototype.hasOwnProperty.call(updates, 'qty'));

    return {
        items,
        newItemsToAdd,
        hasAdditions,
        removalIds,
        hasRemovals,
        activeItemCount,
        itemUpdateEntries,
        hasQtyUpdates,
    };
};

const getEditReasonCodeMessages = ({
    addItemReasonCode,
    hasAdditions,
    hasQtyUpdates,
    hasRemovals,
    qtyReasonCode,
    removeReasonCode,
}) => {
    const reasonCodeErrors = {};
    const addMessage = hasAdditions && !addItemReasonCode ? 'Reason code is required when adding items' : null;
    const removeMessage =
        hasRemovals && !removeReasonCode ? 'Remove reason code is required when removing items' : null;
    const qtyMessage =
        hasQtyUpdates && !qtyReasonCode ? 'Qty reason code is required when changing case quantity' : null;

    if (addMessage) reasonCodeErrors.add = addMessage;
    if (removeMessage) reasonCodeErrors.remove = removeMessage;
    if (qtyMessage) reasonCodeErrors.qty = qtyMessage;

    return {
        addMessage,
        qtyMessage,
        reasonCodeErrors,
        removeMessage,
    };
};

const validateEditAddedItems = ({ itemEditStateRef, newItemsToAdd }) => {
    const itemErrors = [];

    newItemsToAdd.forEach((item, index) => {
        const rowId = String(item?.id ?? '');
        const displaySku = item?.sku ?? '';
        const displayLabel = displaySku ? `SKU ${displaySku}` : `Row ${index + 1}`;
        const itemMessages = [];

        const qtyInput = getItemEditInputValue(itemEditStateRef, rowId, 'qty');
        const unitPriceInput = getItemEditInputValue(itemEditStateRef, rowId, 'unitPrice');
        const csPalletInput = getItemEditInputValue(itemEditStateRef, rowId, 'csPallet');
        const weekInput = getItemEditInputValue(itemEditStateRef, rowId, 'week');
        const yearInput = getItemEditInputValue(itemEditStateRef, rowId, 'year');
        const purchaseTypeInput = getItemEditSelectValue(itemEditStateRef, rowId, 'purchaseType');
        const expectedExpirationDateInput = getItemEditInputValue(itemEditStateRef, rowId, 'expectedExpirationDate');
        const expectedProductionDateInput = getItemEditInputValue(itemEditStateRef, rowId, 'expectedProductionDate');

        const qty = qtyInput === undefined ? item?.qty : qtyInput;
        const unitPrice = unitPriceInput === undefined ? item?.unitPrice : unitPriceInput;
        const csPallet = csPalletInput === undefined ? item?.csPallet : csPalletInput;
        const week = weekInput === undefined ? item?.week : weekInput;
        const year = yearInput === undefined ? item?.year : yearInput;
        const purchaseType = purchaseTypeInput === undefined ? item?.purchaseType : purchaseTypeInput;
        const expectedExpirationDate =
            expectedExpirationDateInput === undefined ? item?.expectedExpirationDate : expectedExpirationDateInput;
        const expectedProductionDate =
            expectedProductionDateInput === undefined ? item?.expectedProductionDate : expectedProductionDateInput;
        const expirationType = String(item?.expirationType ?? '');

        if (!displaySku) {
            itemMessages.push(`Item Master SKU is required for ${displayLabel}`);
        }
        if (!purchaseType) {
            itemMessages.push(`Please select purchase type for ${displayLabel}`);
        }
        const isOpportunistic = String(purchaseType ?? '') === 'OPPORTUNISTIC';
        const requiresExpirationDate = expirationType === 'EXPIRATION_DATE' && isOpportunistic;
        const requiresProductionDate = expirationType === 'PRODUCTION_DATE' && isOpportunistic;

        if (requiresExpirationDate && !expectedExpirationDate) {
            itemMessages.push(`Please fill in expected expiration date for ${displayLabel}`);
        }

        if (requiresProductionDate && !expectedProductionDate) {
            itemMessages.push(`Please fill in expected production date for ${displayLabel}`);
        }
        if (!qty) {
            itemMessages.push(`Please fill in item quantity for ${displayLabel}`);
        }
        if (csPallet !== 0 && !csPallet) {
            itemMessages.push(`Please fill in cases per pallet for ${displayLabel}`);
        }
        if (unitPrice !== 0 && !unitPrice) {
            itemMessages.push(`Please fill in case price for ${displayLabel}`);
        }
        if (!week) {
            itemMessages.push(`Please fill in item week number for ${displayLabel}`);
        }
        if (!year) {
            itemMessages.push(`Please fill in item year for ${displayLabel}`);
        }

        if (itemMessages.length > 0) {
            itemErrors.push({
                id: rowId || `row-${index + 1}`,
                sku: displaySku ? String(displaySku) : '',
                messages: itemMessages,
            });
        }
    });

    return itemErrors;
};

const validateEditUpdatedItems = ({ itemUpdateEntries }) => {
    const itemErrors = [];

    itemUpdateEntries.forEach(([poMapId, updates]) => {
        if (Object.prototype.hasOwnProperty.call(updates, 'purchaseType') && !updates.purchaseType) {
            itemErrors.push({
                id: poMapId,
                sku: String(poMapId),
                messages: ['Please select purchase type for this item'],
            });
        }
    });

    return itemErrors;
};

export const validatePurchaseOrderEdit = ({
    addedItems,
    addItemReasonCode,
    itemEditStateRef,
    itemMastersBySku,
    primaryDraft,
    primaryPurchaseOrder,
    purchaseOrderDetail,
    qtyReasonCode,
    removedItemIds,
    removeReasonCode,
}) => {
    const itemSummaryErrors = [];

    const { currentPrimary, deliveryDate, primaryRequiresReasonCode, scheduledDeliveryRaw } = deriveEditPrimaryState({
        primaryDraft,
        primaryPurchaseOrder,
        purchaseOrderDetail,
    });

    const {
        primaryErrors,
        primaryFieldErrors,
        reasonCodeErrors: primaryReasonCodeErrors,
    } = validateEditPrimaryFields({
        currentPrimary,
        deliveryDate,
        primaryRequiresReasonCode,
        scheduledDeliveryRaw,
    });

    const { activeItemCount, hasAdditions, hasQtyUpdates, hasRemovals, itemUpdateEntries, newItemsToAdd, removalIds } =
        buildEditItemChangeSet({
            addedItems,
            itemEditStateRef,
            purchaseOrderDetail,
            removedItemIds,
        });

    const {
        addMessage,
        qtyMessage,
        reasonCodeErrors: itemReasonCodeErrors,
        removeMessage,
    } = getEditReasonCodeMessages({
        addItemReasonCode,
        hasAdditions,
        hasQtyUpdates,
        hasRemovals,
        qtyReasonCode,
        removeReasonCode,
    });

    if (addMessage) {
        itemSummaryErrors.push(addMessage);
    }
    if (removeMessage) {
        itemSummaryErrors.push(removeMessage);
    }
    if (activeItemCount === 0) {
        itemSummaryErrors.push('At least one item is required');
    }
    if (qtyMessage) {
        itemSummaryErrors.push(qtyMessage);
    }

    const itemErrors = [
        ...validateEditAddedItems({ itemEditStateRef, newItemsToAdd }),
        ...validateEditUpdatedItems({ itemUpdateEntries }),
    ];

    const persistedItems = purchaseOrderDetail?.items ?? purchaseOrderDetail?.purchaseOrderItems ?? [];
    const activePersistedRows = (Array.isArray(persistedItems) ? persistedItems : []).filter((item) => {
        const poMapId = getPoMapId(item);
        return poMapId && !removedItemIds?.has?.(poMapId);
    });
    const activeAddedRows = (addedItems ?? []).filter((item) => !removedItemIds?.has?.(String(item?.id)));
    const duplicateValidation = validateDuplicateLineItems({
        rows: [...activePersistedRows, ...activeAddedRows],
        itemEditStateRef,
        itemMastersBySku,
        removedItemIds: new Set(),
    });
    duplicateValidation.itemSummaryErrors.forEach((message) => {
        if (!itemSummaryErrors.includes(message)) {
            itemSummaryErrors.push(message);
        }
    });
    const mergedItemErrors = mergeItemErrors(itemErrors, duplicateValidation.itemErrors);

    const reasonCodeErrors = {
        ...primaryReasonCodeErrors,
        ...itemReasonCodeErrors,
    };

    const summaryErrors = [
        ...primaryErrors,
        ...itemSummaryErrors,
        ...mergedItemErrors.flatMap((error) => error.messages),
    ];
    const isValid = summaryErrors.length === 0;

    return {
        isValid,
        primaryErrors,
        primaryFieldErrors,
        itemErrors: mergedItemErrors,
        itemSummaryErrors,
        reasonCodeErrors,
        itemUpdateEntries,
        newItemsToAdd,
        removalIds,
        hasAdditions,
        hasRemovals,
        hasQtyUpdates,
        primaryRequiresReasonCode,
        summaryErrors,
    };
};
